// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { publicJwk, thumbprint, type PublicJwk } from "./crypto";

export type SlotRecord = { slot: number; privateKey: CryptoKey; publicKey: CryptoKey; jwk: PublicJwk; jkt: string; accessTokenHash?: string; expiresAt?: string };
let testRecords: Map<number, SlotRecord> | undefined;
const testPending = new Map<number, SlotRecord>();
export const useTestSlotRecords = (records?: Map<number, SlotRecord>): void => { testRecords = records; };
const validSlot = (slot: number) => { if (!Number.isInteger(slot) || slot < 0 || slot > 9) throw new Error("Authentication slot must be between 0 and 9."); };
const database = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = indexedDB.open("nadrama-auth", 2);
  request.onupgradeneeded = () => {
    if (!request.result.objectStoreNames.contains("slots")) request.result.createObjectStore("slots", { keyPath: "slot" });
    if (!request.result.objectStoreNames.contains("pending")) request.result.createObjectStore("pending", { keyPath: "slot" });
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});
const transaction = async <T>(storeName: "slots" | "pending", mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
  const db = await database();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const request = action(tx.objectStore(storeName));
    let result: T;
    request.onsuccess = () => { result = request.result; };
    const failed = () => { db.close(); reject(tx.error ?? request.error ?? new Error("Authentication storage failed.")); };
    request.onerror = failed;
    tx.onerror = failed;
    tx.onabort = failed;
    tx.oncomplete = () => { db.close(); resolve(result); };
  });
};
export const getSlot = async (slot: number): Promise<SlotRecord | undefined> => { validSlot(slot); if (testRecords) return testRecords.get(slot); return transaction("slots", "readonly", (store) => store.get(slot)); };
export const getPendingSlot = async (slot: number): Promise<SlotRecord | undefined> => { validSlot(slot); if (testRecords) return testPending.get(slot); return transaction("pending", "readonly", (store) => store.get(slot)); };
export const clearPendingSlot = async (slot: number): Promise<void> => { validSlot(slot); if (testRecords) { testPending.delete(slot); return; } await transaction("pending", "readwrite", (store) => store.delete(slot)); };
export const createPendingSlot = async (slot: number): Promise<SlotRecord> => {
  validSlot(slot);
  const pair = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, ["sign", "verify"]);
  const jwk = await publicJwk(pair.publicKey);
  const record = { slot, privateKey: pair.privateKey, publicKey: pair.publicKey, jwk, jkt: await thumbprint(jwk) };
  if (testRecords) { testPending.set(slot, record); return record; }
  await transaction("pending", "readwrite", (store) => store.put(record));
  return record;
};
export const promotePendingSlot = async (record: SlotRecord, accessTokenHash: string, expiresAt: string): Promise<void> => {
  const active = { ...record, accessTokenHash, expiresAt };
  if (testRecords) { testPending.delete(record.slot); testRecords.set(record.slot, active); return; }
  const db = await database();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(["slots", "pending"], "readwrite");
    tx.objectStore("slots").put(active);
    tx.objectStore("pending").delete(record.slot);
    const failed = () => { db.close(); reject(tx.error ?? new Error("Authentication storage failed.")); };
    tx.onerror = failed;
    tx.onabort = failed;
    tx.oncomplete = () => { db.close(); resolve(); };
  });
};
export const updateSlot = async (record: SlotRecord, accessTokenHash: string, expiresAt: string): Promise<void> => {
  if (testRecords) { if (testRecords.get(record.slot)?.jkt === record.jkt) testRecords.set(record.slot, { ...record, accessTokenHash, expiresAt }); return; }
  const db = await database();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction("slots", "readwrite");
    const store = tx.objectStore("slots");
    const request = store.get(record.slot);
    request.onsuccess = () => { if ((request.result as SlotRecord | undefined)?.jkt === record.jkt) store.put({ ...record, accessTokenHash, expiresAt }); };
    const failed = () => { db.close(); reject(tx.error ?? request.error ?? new Error("Authentication storage failed.")); };
    request.onerror = failed;
    tx.onerror = failed;
    tx.onabort = failed;
    tx.oncomplete = () => { db.close(); resolve(); };
  });
};
export const clearSlot = async (slot: number): Promise<void> => {
  validSlot(slot);
  if (testRecords) { testPending.delete(slot); testRecords.delete(slot); return; }
  const db = await database();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(["slots", "pending"], "readwrite");
    tx.objectStore("slots").delete(slot);
    tx.objectStore("pending").delete(slot);
    const failed = () => { db.close(); reject(tx.error ?? new Error("Authentication storage failed.")); };
    tx.onerror = failed;
    tx.onabort = failed;
    tx.oncomplete = () => { db.close(); resolve(); };
  });
};
